import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";

/** backend images can be array OR JSON-string OR single string */
const normalizeImages = (images) => {
  if (!images) return [];
  if (Array.isArray(images)) return images;

  if (typeof images === "string") {
    const s = images.trim();
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === "string") return [parsed];
    } catch {
      const cleaned = s.replace(/^"+|"+$/g, "");
      return cleaned ? [cleaned] : [];
    }
  }
  return [];
};

const safe = (v, fb = "-") => (v === null || v === undefined || v === "" ? fb : v);
const money = (v) => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

export default function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [activeImg, setActiveImg] = useState(0);

  const [toast, setToast] = useState("");

  const [relLoading, setRelLoading] = useState(false);
  const [related, setRelated] = useState([]);

  // ✅ CART MAP (same logic as Products page)
  const [cartMap, setCartMap] = useState({});
  const [busyKey, setBusyKey] = useState("");

  // ✅ Desktop-only magnifier: click to enable/disable
  const imgWrapRef = useRef(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const [magEnabled, setMagEnabled] = useState(false); // toggled by click
  const [magPos, setMagPos] = useState({ x: 0, y: 0 }); // within image
  const [magPct, setMagPct] = useState({ px: 50, py: 50 }); // 0-100%
  const MAG_SIZE = 150;
  const ZOOM = 2.8;

  // ✅ origin must NOT have /api
  const IMAGE_ORIGIN = useMemo(() => {
    const base = (axiosInstance.defaults.baseURL || "").trim();
    const clean = base.replace(/\/+$/, "");
    return clean.replace(/\/api$/i, "");
  }, []);

  const imgUrl = (path) => {
    if (!path) return "";
    const v = String(path).trim();
    if (!v) return "";
    if (v.startsWith("http://") || v.startsWith("https://")) return v;
    if (v.startsWith("/")) return `${IMAGE_ORIGIN}${v}`;
    return `${IMAGE_ORIGIN}/${v}`;
  };

  const showToast = (msg) => {
    setToast(msg);
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(""), 2200);
  };

  // ✅ BROADCAST CART COUNT TO NAVBAR
  const broadcastCartCount = (count) => {
    window.dispatchEvent(
      new CustomEvent("cartCountUpdated", {
        detail: { count: Number(count) || 0 },
      })
    );
  };

  // ✅ LOAD CART (same as Products)
  const loadCart = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      // Guest logic (minimal version)
      try {
        const raw = JSON.parse(localStorage.getItem("guest_cart") || "{}");
        const next = {};
        let total = 0;
        for (const [pid, row] of Object.entries(raw)) {
          next[String(pid)] = { cartItemId: pid, qty: row.qty };
          total += Number(row.qty) || 0;
        }
        setCartMap(next);
        broadcastCartCount(total);
      } catch {
        broadcastCartCount(0);
      }
      return;
    }

    try {
      const res = await axiosInstance.get("/api/cart");
      const d = res.data || {};
      const items = (d.CartItems || d.cartItems || d.items || []) ?? [];

      const next = {};
      let total = 0;
      for (const it of items) {
        const prod = it?.product || it?.Product || it?.productDetails || {};
        const pid = prod?.id ?? it?.productId ?? it?.ProductId ?? it?.product_id;
        const cartItemId = it?.id ?? it?.cartItemId ?? it?._id;
        const qty = Number(it?.qty ?? it?.quantity ?? it?.cartQty ?? 1);

        if (pid != null) {
          next[String(pid)] = { cartItemId, qty };
          total += qty;
        }
      }
      setCartMap(next);
      broadcastCartCount(total);
    } catch {
      broadcastCartCount(0);
    }
  };

  // ✅ try load cart on mount if token
  useEffect(() => {
    loadCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ detect desktop
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 901);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ✅ ESC to close magnifier
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setMagEnabled(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // -------- LOAD PRODUCT ----------
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setErr("");
        setP(null);
        setActiveImg(0);
        setMagEnabled(false);
        setRelated([]);

        const res = await axiosInstance.get(`/api/products/${id}`);
        const item = res?.data?.product || res?.data || null;

        if (!mounted) return;
        setP(item);
        setActiveImg(0);

        // refresh cart (optional) when new product loads
        loadCart();
      } catch (e) {
        if (!mounted) return;
        setErr(
          e?.response?.data?.message ||
            e?.response?.data?.msg ||
            e?.message ||
            "Failed to load product"
        );
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => (mounted = false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // -------- IMAGES ----------
  const images = useMemo(() => {
    const arr = normalizeImages(p?.images);
    const out = arr.map(imgUrl).filter(Boolean);
    if (out.length) return out;
    if (p?.image) return [imgUrl(p.image)].filter(Boolean);
    return [];
  }, [p, IMAGE_ORIGIN]);

  useEffect(() => {
    if (activeImg >= images.length) setActiveImg(0);
  }, [images.length, activeImg]);

  const mainImage =
    images[activeImg] || images[0] || "https://via.placeholder.com/900x900?text=No+Image";

  const inStock = Number(p?.stockQty || 0) > 0;

  // -------- RELATED ----------
  useEffect(() => {
    let mounted = true;

    const loadRelated = async () => {
      if (!p) return;

      const subCategoryId = p?.subCategoryId || p?.subCategory?.id || null;
      const subCategoryName = p?.subCategoryName || "";
      const categoryName = p?.categoryName || "";

      try {
        setRelLoading(true);

        let res;
        if (subCategoryId) {
          res = await axiosInstance.get("/api/products", { params: { subCategoryId } });
        } else if (subCategoryName) {
          res = await axiosInstance.get("/api/products", { params: { subCategoryName } });
        } else if (categoryName) {
          res = await axiosInstance.get("/api/products", { params: { categoryName } });
        } else {
          res = await axiosInstance.get("/api/products");
        }

        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.products)
          ? res.data.products
          : Array.isArray(res.data?.data)
          ? res.data.data
          : [];

        const filtered = list
          .filter((x) => String(x?.id) !== String(p?.id))
          .filter((x) => (x?.isActive ?? true) === true)
          .slice(0, 10);

        if (!mounted) return;
        setRelated(filtered);
      } catch {
        if (!mounted) return;
        setRelated([]);
      } finally {
        if (mounted) setRelLoading(false);
      }
    };

    loadRelated();
    return () => (mounted = false);
  }, [p?.id, p?.subCategoryId, p?.subCategoryName, p?.categoryName]);

  // -------- CART ACTIONS (same as Products) ----------
  const addToCart = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!p?.id) return;

    const token = localStorage.getItem("token");
    if (!token) {
      // guest add
      try {
        const raw = JSON.parse(localStorage.getItem("guest_cart") || "{}");
        const pid = String(p.id);
        if (raw[pid]) raw[pid].qty += 1;
        else raw[pid] = { qty: 1, product: p };
        localStorage.setItem("guest_cart", JSON.stringify(raw));
        window.dispatchEvent(new Event("guestCartUpdated"));
        await loadCart();
        showToast("🛒 Added to guest cart");
      } catch {
        showToast("❌ Guest add failed");
      }
      return;
    }

    try {
      setBusyKey(`add:${p.id}`);
      await axiosInstance.post("/api/cart", { productId: p.id });
      await loadCart();
      showToast("✅ Added to cart");
    } catch (er) {
      const msg = er?.response?.data?.msg || er?.response?.data?.message || er?.message || "Add to cart failed";
      showToast(`❌ ${msg}`);
    } finally {
      setBusyKey("");
    }
  };

  const updateCartQty = async (e, newQty) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!p?.id) return;

    const pid = String(p.id);
    const row = cartMap[pid];
    const token = localStorage.getItem("token");

    if (!token) {
      // guest update
      try {
        const raw = JSON.parse(localStorage.getItem("guest_cart") || "{}");
        if (newQty < 1) delete raw[pid];
        else {
          if (!raw[pid]) raw[pid] = { qty: newQty, product: p };
          else raw[pid].qty = newQty;
        }
        localStorage.setItem("guest_cart", JSON.stringify(raw));
        window.dispatchEvent(new Event("guestCartUpdated"));
        await loadCart();
        if (newQty < 1) showToast("🗑 Removed");
      } catch {
        showToast("❌ Guest qty update failed");
      }
      return;
    }

    if (!row?.cartItemId) {
      if (newQty >= 1) return addToCart(e);
      return;
    }
    if (newQty < 1) {
      try {
        setBusyKey(`qty:${p.id}`);
        await axiosInstance.delete(`/api/cart/${row.cartItemId}`);
        await loadCart(); // use loadCart to ensure sync
        showToast("🗑 Item removed from cart");
      } catch (er) {
        showToast(`❌ ${er?.response?.data?.msg || er?.message || "Removal failed"}`);
        await loadCart();
      } finally {
        setBusyKey("");
      }
      return;
    }
    try {
      setBusyKey(`qty:${p.id}`);
      await axiosInstance.put(`/api/cart/${row.cartItemId}`, { qty: newQty });
      await loadCart(); // use loadCart to ensure sync
    } catch (er) {
      const msg = er?.response?.data?.msg || er?.response?.data?.message || er?.message || "Qty update failed";
      showToast(`❌ ${msg}`);
      await loadCart();
    } finally {
      setBusyKey("");
    }
  };

  // -------- MAGNIFIER HELPERS ----------
  const updateMagnifierFromClientXY = (clientX, clientY) => {
    const wrap = imgWrapRef.current;
    if (!wrap) return;

    const rect = wrap.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    const y = Math.min(Math.max(clientY - rect.top, 0), rect.height);

    const px = (x / rect.width) * 100;
    const py = (y / rect.height) * 100;

    setMagPos({ x, y });
    setMagPct({ px, py });
  };

  const onImageClick = (e) => {
    if (!isDesktop) return; // ✅ desktop only
    setMagEnabled((v) => !v);
    updateMagnifierFromClientXY(e.clientX, e.clientY);
  };

  const onMouseMove = (e) => {
    if (!isDesktop || !magEnabled) return;
    updateMagnifierFromClientXY(e.clientX, e.clientY);
  };

  const onMouseLeave = () => {
    // keep enabled until user closes; do not auto-close
  };

  // lens bounding
  const wrapW = imgWrapRef.current?.clientWidth || 0;
  const wrapH = imgWrapRef.current?.clientHeight || 0;
  const lensLeft = Math.max(0, Math.min(magPos.x - MAG_SIZE / 2, wrapW - MAG_SIZE));
  const lensTop = Math.max(0, Math.min(magPos.y - MAG_SIZE / 2, wrapH - MAG_SIZE));

  const bgSize = `${ZOOM * 100}% ${ZOOM * 100}%`;
  const bgPos = `${magPct.px}% ${magPct.py}%`;

  const breadCat = safe(p?.categoryName, p?.category || "Home");
  const breadSub = safe(p?.subCategoryName, "");

  // ✅ product qty from cart
  const pidStr = String(p?.id ?? "");
  const qty = Number(cartMap?.[pidStr]?.qty || 0);
  const isBusy = busyKey === `add:${p?.id}` || busyKey === `qty:${p?.id}`;

  if (loading) {
    return (
      <div className="pdPage">
        <style>{css}</style>
        <div className="pdWrap">
          <div className="cardBox infoBox">Loading…</div>
        </div>
      </div>
    );
  }
  if (err) {
    return (
      <div className="pdPage">
        <style>{css}</style>
        <div className="pdWrap">
          <div className="cardBox infoBox errBox">{err}</div>
        </div>
      </div>
    );
  }
  if (!p) {
    return (
      <div className="pdPage">
        <style>{css}</style>
        <div className="pdWrap">
          <div className="cardBox infoBox">Product not found.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="pdPage">
      <style>{css}</style>

      <div className="pdWrap">
      

        {/* MAIN GRID */}
        <div className={"pdGrid" + (isDesktop && magEnabled ? " hasMag" : "")}>
          {/* LEFT */}
          <div className="leftCol">
            <div className="cardBox">
              <div
                className={"imgWrap" + (isDesktop ? " clickable" : "")}
                ref={imgWrapRef}
                onClick={onImageClick}
                onMouseMove={onMouseMove}
                onMouseLeave={onMouseLeave}
                title={isDesktop ? "Click to toggle magnifier" : ""}
              >
                <img
                  className="mainImg"
                  src={mainImage}
                  alt={p?.name || "Product"}
                  onError={(e) =>
                    (e.currentTarget.src = "https://via.placeholder.com/900x900?text=No+Image")
                  }
                />

                {/* ✅ only show lens when magnifier enabled */}
                {isDesktop && magEnabled && (
                  <div
                    className="lens"
                    style={{ width: MAG_SIZE, height: MAG_SIZE, left: lensLeft, top: lensTop }}
                  />
                )}

                {/* ✅ small hint */}
                {isDesktop && !magEnabled && <div className="clickHint">Click image to magnify</div>}

                {/* ✅ close button when enabled */}
                {isDesktop && magEnabled && (
                  <button
                    type="button"
                    className="magClose"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMagEnabled(false);
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* THUMBS */}
              {images.length > 1 && (
                <div className="thumbRow">
                  {images.map((u, idx) => (
                    <button
                      key={u + idx}
                      type="button"
                      className={"thumb" + (idx === activeImg ? " active" : "")}
                      onClick={() => {
                        setActiveImg(idx);
                        setMagEnabled(false);
                      }}
                    >
                      <img
                        src={u}
                        alt={"thumb-" + idx}
                        onError={(e) => (e.currentTarget.src = "https://via.placeholder.com/150?text=No")}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: DETAILS (always visible) */}
          <div className="rightCol">
            <div className="cardBox rightCard">
              <div className="crumbs">
                Home <span className="sep">/</span> {breadCat}
                {breadSub ? (
                  <>
                    <span className="sep">/</span> {breadSub}
                  </>
                ) : null}
              </div>

              <div className="title">{safe(p?.name)}</div>
              <div className="subLine">{safe(p?.packSize, "1")} pc</div>

              <div className="priceRow">
                <div>
                  <div className="price">₹{money(p?.price)}</div>
                  {!!p?.mrp && <div className="mrp">MRP ₹{money(p?.mrp)}</div>}
                  <div className="tax">(Inclusive of all taxes)</div>
                </div>

                {/* ✅ BUTTON AREA REPLACED WITH ADD / QTY CONTROLS */}
                {!inStock ? (
                  <button className="addBtn" type="button" disabled>
                    Out of stock
                  </button>
                ) : qty <= 0 ? (
                  <button className="addBtn" type="button" onClick={addToCart} disabled={isBusy}>
                    {isBusy ? "Adding..." : "ADD"}
                  </button>
                ) : (
                  <div className="qtyBox" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="qtyBtn"
                      disabled={isBusy}
                      onClick={(e) => updateCartQty(e, qty - 1)}
                      title="Decrease"
                    >
                      −
                    </button>
                    <div className="qtyVal">{qty}</div>
                    <button
                      type="button"
                      className="qtyBtn"
                      disabled={isBusy}
                      onClick={(e) => updateCartQty(e, qty + 1)}
                      title="Increase"
                    >
                      +
                    </button>
                  </div>
                )}
              </div>

              {inStock ? (
                <div ></div>
              ) : (
                <div className="stockNo">Out of stock</div>
              )}

              <div className="chipGrid">
                <div className="chip">
                  <span>Brand</span>
                  <b>{safe(p?.brand)}</b>
                </div>
                <div className="chip">
                  <span>Manufacturer</span>
                  <b>{safe(p?.manufacturer)}</b>
                </div>
                <div className="chip">
                  <span>Category</span>
                  <b>{breadCat}</b>
                </div>
                <div className="chip">
                  <span>Subcategory</span>
                  <b>{breadSub || "-"}</b>
                </div>
              </div>

              <div className="whyBox">
                <div className="whyItem">
                  <div className="whyIcon">🏷️</div>
                  <div>
                    <div className="whyH">Best Prices & Offers</div>
                    <div className="whyP">Best price destination with offers directly from manufacturers.</div>
                  </div>
                </div>

                <div className="whyItem">
                  <div className="whyIcon">🧺</div>
                  <div>
                    <div className="whyH">Wide Assortment</div>
                    <div className="whyP">Choose from a wide range of products across categories.</div>
                  </div>
                </div>
              </div>

              <div className="descBox">
                <div className="descTitle">Product Details</div>
                <div className="descText">{safe(p?.description, "No description")}</div>
              </div>
            </div>
          </div>

          {/* ✅ DESKTOP ONLY MAGNIFIER BOX (visible only after click) */}
          {isDesktop && magEnabled && (
            <div className="magDock cardBox">
              <div className="magDockHead">
                <div className="magDockTitle">Magnified View</div>
                <div className="magDockSub">Move mouse on image</div>
              </div>

              <div className="magDockBox">
                <div
                  className="magView"
                  style={{
                    backgroundImage: `url(${mainImage})`,
                    backgroundSize: bgSize,
                    backgroundPosition: bgPos,
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* RELATED BELOW */}
        <div className="relWrap">
          <div className="relHead">
            <div>
              <div className="relTitle">Related Products</div>
              <div className="relSub">More items you may like</div>
            </div>
          </div>

          {relLoading ? (
            <div className="cardBox infoBox">Loading related products…</div>
          ) : related.length === 0 ? (
            <div className="cardBox infoBox">No related products found.</div>
          ) : (
            <div className="relGrid">
              {related.map((rp) => {
                const rImgs = normalizeImages(rp?.images);
                const thumb = imgUrl(rImgs?.[0]) || imgUrl(rp?.image);

                return (
                  <button
                    key={rp.id}
                    type="button"
                    className="relCard"
                    onClick={() => navigate(`/productdetails/${rp.id}`)}
                    title={rp?.name}
                  >
                    <div className="relImgWrap">
                      <img
                        src={thumb || "https://via.placeholder.com/400x300?text=No+Image"}
                        alt={rp?.name}
                        onError={(e) => (e.currentTarget.src = "https://via.placeholder.com/400x300?text=No+Image")}
                      />
                      {rp?.badge ? <div className="badge">{rp.badge}</div> : null}
                    </div>

                    <div className="relBody">
                      <div className="relName">{safe(rp?.name)}</div>
                      <div className="relMeta">
                        <span className="relBrand">{safe(rp?.brand, "")}</span>
                        <span className="relPrice">₹{money(rp?.price)}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const css = `
  *{ box-sizing:border-box; }
  body{ margin:0; font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto; background:#fff; color:#101828; }

  .pdPage{ min-height:100vh; background:#fff; }
  .pdWrap{ width:min(1200px, 100%); margin:0 auto; padding: 18px 16px 40px; }

  .cardBox{
    background:#fff;
    border:1px solid rgba(16,24,40,.10);
    border-radius: 18px;
    box-shadow: 0 10px 24px rgba(0,0,0,.06);
    overflow:hidden;
  }

  .infoBox{ padding: 14px 16px; font-weight: 800; color: rgba(16,24,40,.75); }
  .errBox{ background: rgba(255,0,0,.05); border-color: rgba(255,0,0,.16); color:#8a1f1f; }

  .topRow{
    display:flex; justify-content:space-between; align-items:center;
    gap: 12px; margin-bottom: 14px; flex-wrap: wrap;
  }
  .backBtn{
    border:1px solid rgba(16,24,40,.14); background:#fff;
    padding:10px 12px; border-radius: 12px; cursor:pointer;
    font-weight: 900; color:#101828;
  }
  .toast{
    padding:10px 12px; border-radius: 12px;
    background:#f3f6ff; border:1px solid rgba(34,139,230,.25);
    color:#0b4f9a; font-weight: 900; font-size: 13px;
  }

  /* ✅ main grid: image + details; magnifier dock appears as third column only when enabled */
  .pdGrid{
    display:grid;
    grid-template-columns: 1.05fr .95fr;
    gap: 18px;
    align-items:start;
  }
  @media (min-width: 901px){
    .pdGrid.hasMag{ grid-template-columns: 1.05fr .85fr .70fr; }
  }
  @media (max-width: 900px){
    .pdGrid{ grid-template-columns: 1fr; }
  }

  /* LEFT */
  .imgWrap{
    position: relative;
    width:100%;
    height: 520px;
    background:#fff;
    display:flex;
    align-items:center;
    justify-content:center;
    overflow:hidden;
  }
  @media (max-width: 900px){
    .imgWrap{ height: 420px; }
  }
  .imgWrap.clickable{ cursor: zoom-in; }
  .mainImg{
    width:100%;
    height:100%;
    object-fit: contain;
    display:block;
    background:#fff;
  }

  .lens{
    position:absolute;
    border-radius: 16px;
    border: 2px solid rgba(34,139,230,.55);
    background: rgba(34,139,230,.12);
    box-shadow: 0 14px 26px rgba(0,0,0,.10);
    pointer-events:none;
  }

  .clickHint{
    position:absolute;
    bottom: 12px;
    left: 12px;
    padding: 8px 10px;
    border-radius: 999px;
    background: rgba(255,255,255,.92);
    border: 1px solid rgba(16,24,40,.12);
    color: rgba(16,24,40,.75);
    font-weight: 900;
    font-size: 12px;
  }

  .magClose{
    position:absolute;
    top: 12px;
    right: 12px;
    border:1px solid rgba(16,24,40,.14);
    background:#fff;
    padding: 8px 10px;
    border-radius: 12px;
    cursor:pointer;
    font-weight: 950;
  }

  .thumbRow{
    padding: 12px;
    display:flex;
    gap: 10px;
    overflow-x:auto;
  }
  .thumbRow::-webkit-scrollbar{ height: 8px; }
  .thumb{
    width: 74px; height: 74px;
    border-radius: 14px;
    border: 2px solid transparent;
    background:#fff;
    padding: 6px;
    cursor:pointer;
    flex: 0 0 auto;
    box-shadow: 0 8px 18px rgba(0,0,0,.06);
  }
  .thumb.active{ border-color: rgba(34,139,230,.55); }
  .thumb img{ width:100%; height:100%; object-fit: contain; display:block; }

  /* RIGHT */
  .rightCard{ padding: 16px; }
  .crumbs{ font-size: 13px; color: rgba(16,24,40,.65); font-weight: 700; }
  .sep{ margin:0 6px; color: rgba(16,24,40,.35); }

  .title{ margin-top: 10px; font-size: 24px; font-weight: 950; color:#101828; line-height:1.2; }
  .subLine{ margin-top: 10px; font-size: 14px; color: rgba(16,24,40,.75); font-weight: 800; }

  .priceRow{
    margin-top: 14px;
    display:flex;
    justify-content:space-between;
    align-items:center;
    gap: 12px;
    flex-wrap: wrap;
  }
  .price{ font-size: 22px; font-weight: 950; color:#101828; }
  .mrp{ margin-top: 6px; font-size: 12px; font-weight: 900; color: rgba(16,24,40,.55); text-decoration: line-through; }
  .tax{ margin-top: 4px; font-size: 12px; color: rgba(16,24,40,.55); font-weight: 700; }

  .addBtn{
    background: #1f7a1f;
    color:#fff;
    border:0;
    padding: 12px 18px;
    border-radius: 12px;
    font-weight: 950;
    cursor:pointer;
    min-width: 150px;
  }
  .addBtn:disabled{ opacity:.55; cursor:not-allowed; }

  /* ✅ QTY CONTROL (added, design matches) */
  .qtyBox{
    min-width: 150px;
    display:grid;
    grid-template-columns: 44px 1fr 44px;
    align-items:center;
    border-radius: 12px;
    overflow:hidden;
    border: 1px solid rgba(16,24,40,.14);
    background: rgba(255,255,255,.96);
    box-shadow: 0 10px 22px rgba(0,0,0,.06);
  }
  .qtyBtn{
    height: 42px;
    border: none;
    background: rgba(16,24,40,.06);
    color: #101828;
    font-size: 20px;
    font-weight: 950;
    cursor: pointer;
  }
  .qtyBtn:disabled{ opacity:.55; cursor:not-allowed; }
  .qtyVal{
    height: 42px;
    display:grid;
    place-items:center;
    font-weight: 950;
    color: #101828;
  }

  .stockOk{
    margin-top: 12px;
    display:inline-block;
    padding: 8px 10px;
    border-radius: 14px;
    background: rgba(16,185,129,.10);
    border: 1px solid rgba(16,185,129,.22);
    color:#065f46;
    font-weight: 900;
    font-size: 12px;
  }
  .stockNo{
    margin-top: 12px;
    display:inline-block;
    padding: 8px 10px;
    border-radius: 14px;
    background: rgba(239,68,68,.10);
    border: 1px solid rgba(239,68,68,.22);
    color:#7f1d1d;
    font-weight: 900;
    font-size: 12px;
  }

  .chipGrid{
    margin-top: 14px;
    display:grid;
    grid-template-columns: repeat(2, minmax(0,1fr));
    gap: 10px;
  }
  @media (max-width: 520px){
    .chipGrid{ grid-template-columns: 1fr; }
  }
  .chip{
    border-radius: 16px;
    border:1px solid rgba(16,24,40,.10);
    background:#fbfcff;
    padding: 10px 12px;
  }
  .chip span{
    display:block;
    font-size: 12px;
    color: rgba(16,24,40,.55);
    font-weight: 900;
  }
  .chip b{
    display:block;
    margin-top: 6px;
    font-size: 13px;
    color:#101828;
    font-weight: 950;
    word-break: break-word;
  }

  .whyBox{ margin-top: 18px; padding-top: 16px; border-top: 1px solid rgba(16,24,40,.10); }
  .whyTitle{ font-size: 18px; font-weight: 950; margin-bottom: 12px; }
  .whyItem{ display:grid; grid-template-columns: 52px 1fr; gap: 12px; align-items:center; margin-bottom: 12px; }
  .whyIcon{
    width: 46px; height: 46px; border-radius: 999px;
    background:#fff7d6; border: 1px solid rgba(245,158,11,.25);
    display:flex; align-items:center; justify-content:center; font-size: 20px;
  }
  .whyH{ font-weight: 950; font-size: 14px; }
  .whyP{ margin-top: 3px; color: rgba(16,24,40,.60); font-weight: 700; font-size: 13px; line-height: 1.4; }

  .descBox{ margin-top: 14px; padding-top: 14px; border-top: 1px solid rgba(16,24,40,.10); }
  .descTitle{ font-size: 16px; font-weight: 950; margin-bottom: 8px; }
  .descText{ color: rgba(16,24,40,.78); font-weight: 700; line-height: 1.6; }

  /* ✅ Magnifier dock (right side) */
  .magDock{
    display:none;
  }
  @media (min-width: 901px){
    .magDock{
      display:block;
      position: sticky;
      top: 16px;
      height: fit-content;
      padding: 12px;
    }
  }
  .magDockHead{ margin-bottom: 10px; }
  .magDockTitle{ font-weight: 950; color:#101828; }
  .magDockSub{ margin-top: 4px; font-size: 12px; font-weight: 800; color: rgba(16,24,40,.55); }

  .magDockBox{
    height: 360px;
    border-radius: 16px;
    border: 1px solid rgba(16,24,40,.10);
    overflow:hidden;
    background:#fff;
  }
  .magView{
    width:100%;
    height:100%;
    background-repeat:no-repeat;
  }

  /* RELATED */
  .relWrap{ margin-top: 18px; }
  .relHead{ display:flex; justify-content:space-between; align-items:baseline; gap: 10px; margin-bottom: 10px; flex-wrap: wrap; }
  .relTitle{ font-size: 18px; font-weight: 950; color:#101828; }
  .relSub{ margin-top: 4px; font-size: 12px; color: rgba(16,24,40,.60); font-weight: 800; }

  .relGrid{
    display:grid;
    grid-template-columns: repeat(5, minmax(0,1fr));
    gap: 12px;
  }
  @media (max-width: 1200px){
    .relGrid{ grid-template-columns: repeat(4, minmax(0,1fr)); }
  }
  @media (max-width: 900px){
    .relGrid{ grid-template-columns: repeat(3, minmax(0,1fr)); }
  }
  @media (max-width: 640px){
    .relGrid{ grid-template-columns: repeat(2, minmax(0,1fr)); }
  }

  .relCard{
    padding: 0;
    border:1px solid rgba(16,24,40,.10);
    background:#fff;
    border-radius: 16px;
    overflow:hidden;
    cursor:pointer;
    text-align:left;
    box-shadow: 0 10px 22px rgba(0,0,0,.06);
    transition: transform .12s ease, box-shadow .12s ease;
  }
  .relCard:hover{ transform: translateY(-1px); box-shadow: 0 14px 26px rgba(0,0,0,.08); }

  .relImgWrap{
    position: relative;
    height: 150px;
    background:#fff;
    display:flex;
    align-items:center;
    justify-content:center;
    border-bottom: 1px solid rgba(16,24,40,.06);
  }
  .relImgWrap img{ width:100%; height:100%; object-fit: contain; display:block; }
  .badge{
    position:absolute; top:10px; left:10px;
    padding: 6px 10px;
    border-radius: 999px;
    background: rgba(34,139,230,.92);
    color:#fff;
    font-weight: 950;
    font-size: 11px;
    box-shadow: 0 10px 18px rgba(34,139,230,.22);
  }
  .relBody{ padding: 12px; }
  .relName{
    font-weight: 950;
    font-size: 13px;
    color:#101828;
    line-height:1.2;
    min-height: 32px;
    display:-webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow:hidden;
  }
  .relMeta{
    margin-top: 10px;
    display:flex;
    justify-content: space-between;
    gap: 10px;
    align-items:center;
    font-size: 12px;
  }
  .relBrand{
    color: rgba(16,24,40,.60);
    font-weight: 800;
    overflow:hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .relPrice{ color:#101828; font-weight: 950; }
`;